import Button from '@/storybook/components/Button/Button';
import cx from 'classnames';
import { ReactNode, useCallback, useState } from 'react';
import { ButtonStandardSize } from '../Button/Button';
import styles from './ToggleButtonGroup.module.css';

export interface ToggleButtonOption<T> {
  label: string;
  value: T;
  icon?: ReactNode;
  disabled?: boolean;
  dataTestIdPrefix?: string;
}

export interface ToggleButtonGroupProps<T> {
  size?: ButtonStandardSize;
  options: ToggleButtonOption<T>[];
  value?: T;
  defaultValue?: T;
  onChange?: (value: T) => void;
  className?: string;
}

export default function ToggleButtonGroup<T>({
  options,
  value: controlledValue,
  defaultValue,
  onChange,
  className,
  size,
}: ToggleButtonGroupProps<T>) {
  const [uncontrolledValue, setUncontrolledValue] = useState<T | undefined>(defaultValue);
  const selectedValue = controlledValue ?? uncontrolledValue;

  const handleClick = useCallback(
    (val: T) => {
      if (!controlledValue) setUncontrolledValue(val);
      onChange?.(val);
    },
    [controlledValue, onChange],
  );

  const handleOptionClick = useCallback((val: T) => () => handleClick(val), [handleClick]);

  return (
    <div className={cx(styles.toggleButtonGroup, className)}>
      {options.map(option => (
        <Button
          key={String(option.value)}
          variant="outlined"
          size={size}
          startIcon={option.icon}
          className={cx(styles.toggleButton, { [styles.active]: selectedValue === option.value })}
          disabled={option.disabled}
          onClick={handleOptionClick(option.value)}
          data-testid={option.dataTestIdPrefix}
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
}
